import { useState, useEffect, useRef } from 'react'

import { ActionIcon, Box, Menu, NumberInput, TextInput } from '@mantine/core'
import dayjs from 'dayjs'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DateFilter from 'src/components/Inputs/DateFilter/DateFilter'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import IconFilter from 'src/icons/IconFilter'
import { formatSnakeValue } from 'src/lib/formatters'

import { ApplicantFilter, FilterOption, RangeValue } from './types'
import ZipcodeFilter from './ZipcodeFilter'

interface ApplicantFilterMenuProps {
  filters: ApplicantFilter[]
  onClearAll: () => void
}

const ApplicantFilterMenu = ({
  filters,
  onClearAll,
}: ApplicantFilterMenuProps) => {
  const [selectedSubmenu, setSelectedSubmenu] = useState('')
  const [localMin, setLocalMin] = useState<number | string>('')
  const [localMax, setLocalMax] = useState<number | string>('')
  const [searchText, setSearchText] = useState('')
  const [filterCategorySearch, setFilterCategorySearch] = useState('')
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set())
  const [expandedApplicantStages, setExpandedApplicantStages] = useState<
    Set<string>
  >(new Set())
  const [expandedLeadSources, setExpandedLeadSources] = useState<Set<string>>(
    new Set()
  )
  const hasInitializedStepsExpansion = useRef(false)
  const hasInitializedApplicantStagesExpansion = useRef(false)
  const hasInitializedLeadSourcesExpansion = useRef(false)
  // Refs for paginated filters' load-more sentinel — see the
  // IntersectionObserver effect below. scrollContainerRef is the IO root;
  // loadMoreSentinelRef is rendered at the bottom of the options list and
  // fires onLoadMore() as it scrolls into view.
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)

  // Initialize expanded steps when steps submenu is first opened
  useEffect(() => {
    if (selectedSubmenu === 'steps') {
      // Only initialize on first open, not on every filter change
      if (!hasInitializedStepsExpansion.current) {
        const stepsFilter = filters.find((f) => f.urlKey === 'steps')
        if (stepsFilter && stepsFilter.selected) {
          const parentStepsToExpand = new Set<string>()
          stepsFilter.selected.forEach((selected) => {
            if (typeof selected === 'string' && selected.includes(':')) {
              // If it's a sub-item (contains ':'), extract the parent
              const [parent] = selected.split(':')
              parentStepsToExpand.add(parent)
            }
          })
          setExpandedSteps(parentStepsToExpand)
        }
        hasInitializedStepsExpansion.current = true
      }
    } else if (
      selectedSubmenu !== 'steps' &&
      hasInitializedStepsExpansion.current
    ) {
      // Reset expansion state and initialization flag when leaving steps submenu
      setExpandedSteps(new Set())
      hasInitializedStepsExpansion.current = false
    }
  }, [selectedSubmenu, filters])

  // Initialize expanded applicant stages when applicant stage submenu is first opened
  useEffect(() => {
    if (selectedSubmenu === 'applicantStages') {
      // Only initialize on first open, not on every filter change
      if (!hasInitializedApplicantStagesExpansion.current) {
        const applicantStageFilter = filters.find(
          (f) => f.urlKey === 'applicantStages'
        )
        if (applicantStageFilter && applicantStageFilter.selected) {
          const parentStagesToExpand = new Set<string>()
          applicantStageFilter.selected.forEach((selected) => {
            if (typeof selected === 'string') {
              if (selected.includes(':')) {
                // If it's a sub-item (contains ':'), extract the parent
                const [parent] = selected.split(':')
                parentStagesToExpand.add(parent)
              } else {
                // If it's a parent item (no ':'), expand it
                parentStagesToExpand.add(selected)
              }
            }
          })
          setExpandedApplicantStages(parentStagesToExpand)
        }
        hasInitializedApplicantStagesExpansion.current = true
      }
    } else if (
      selectedSubmenu !== 'applicantStages' &&
      hasInitializedApplicantStagesExpansion.current
    ) {
      // Reset expansion state and initialization flag when leaving applicant stage submenu
      setExpandedApplicantStages(new Set())
      hasInitializedApplicantStagesExpansion.current = false
    }
  }, [selectedSubmenu, filters])

  // Initialize expanded lead sources when lead source submenu is first opened
  useEffect(() => {
    if (selectedSubmenu === 'leadSources') {
      // Only initialize on first open, not on every filter change
      if (!hasInitializedLeadSourcesExpansion.current) {
        const leadSourceFilter = filters.find((f) => f.urlKey === 'leadSources')
        if (leadSourceFilter && leadSourceFilter.selected) {
          const parentTypesToExpand = new Set<string>()
          leadSourceFilter.selected.forEach((selected) => {
            if (typeof selected === 'string') {
              if (selected.includes(':')) {
                // If it's a sub-item (TYPE:name), extract the parent link type
                const [parent] = selected.split(':')
                parentTypesToExpand.add(parent)
              } else {
                // If it's a parent item (bare link type), expand it
                parentTypesToExpand.add(selected)
              }
            }
          })
          setExpandedLeadSources(parentTypesToExpand)
        }
        hasInitializedLeadSourcesExpansion.current = true
      }
    } else if (
      selectedSubmenu !== 'leadSources' &&
      hasInitializedLeadSourcesExpansion.current
    ) {
      // Reset expansion state and initialization flag when leaving submenu
      setExpandedLeadSources(new Set())
      hasInitializedLeadSourcesExpansion.current = false
    }
  }, [selectedSubmenu, filters])

  // Initialize local values when a range filter is opened
  useEffect(() => {
    if (selectedSubmenu) {
      const activeFilter = filters.find((f) => f.urlKey === selectedSubmenu)
      if (activeFilter?.isRange && activeFilter.rangeValue) {
        setLocalMin(activeFilter.rangeValue.gte ?? '')
        setLocalMax(activeFilter.rangeValue.lte ?? '')
      }
    }
  }, [selectedSubmenu, filters])

  // `filters` re-references whenever any sub-hook's options update. Keeping a
  // ref lets the submenu-exit cleanup below read the latest filter list
  // without re-running on every options change (which is what caused the
  // refetch loop fixed in the prior commit).
  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  // Wire up infinite-scroll for the active filter if it supports load-more.
  // Re-runs when hasMore, the loader callback identity, or the options count
  // change. The options-count dep is defensive: IntersectionObserver only
  // fires when the intersection state crosses the threshold, so if a layout
  // ever lets the sentinel stay intersecting after an append (small items in
  // a tall container, virtualization, etc.) the observer would otherwise sit
  // silent. Re-creating after each append forces a fresh intersection check.
  const activeSubmenuFilter = filters.find((f) => f.urlKey === selectedSubmenu)
  const activeOnLoadMore = activeSubmenuFilter?.onLoadMore
  const activeHasMore = activeSubmenuFilter?.hasMore
  const activeOptionsCount = activeSubmenuFilter?.options?.length ?? 0
  useEffect(() => {
    if (!activeOnLoadMore || !activeHasMore) return
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          activeOnLoadMore()
        }
      },
      { root: scrollContainerRef.current, rootMargin: '0px', threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [selectedSubmenu, activeOnLoadMore, activeHasMore, activeOptionsCount])

  // Clear search text when leaving a submenu, and reset the outgoing
  // submenu's server-side search (if it has one) synchronously. Without the
  // reset, a user who searched "Terre Haute" in Position then closed the
  // submenu would reopen it with the search box empty but options still
  // narrowed to the prior match. Calling resetToInitial (rather than
  // onSearchChange('')) skips the 250ms debounce so re-opening within that
  // window still kicks off the reset query immediately.
  // Decoupled from `filters` so options updates don't retrigger this effect.
  useEffect(() => {
    setSearchText('')
    const submenuOnEntry = selectedSubmenu
    return () => {
      if (submenuOnEntry) {
        const outgoingFilter = filtersRef.current.find(
          (f) => f.urlKey === submenuOnEntry
        )
        outgoingFilter?.resetToInitial?.()
      }
    }
  }, [selectedSubmenu])

  const getTotalSelectedCount = () => {
    return filters.reduce((total, filter) => {
      if (filter.isRange && filter.rangeValue) {
        // Count range filters if they have any values set
        const hasRangeValue =
          filter.rangeValue.gte !== null || filter.rangeValue.lte !== null
        return total + (hasRangeValue ? 1 : 0)
      }
      if (filter.isDate && filter.dateValue) {
        // Count date filters if they have any values set
        const hasDateValue =
          filter.dateValue.gte !== null || filter.dateValue.lte !== null
        return total + (hasDateValue ? 1 : 0)
      }
      const rangeCount = hasDateRangeValue(filter) ? 1 : 0
      return total + (filter.selected?.length || 0) + rangeCount
    }, 0)
  }

  const hasDateRangeValue = (filter: ApplicantFilter) =>
    !!(filter.dateRange?.value.gte || filter.dateRange?.value.lte)

  const getDisplayValue = (filter: ApplicantFilter, value: string) => {
    if (filter.formatValue) {
      return filter.formatValue(value)
    }
    if (filter.options?.find) {
      return filter.options.find((opt) => opt.value === value)?.label
    }
    return formatSnakeValue(value)
  }

  const getRangeDisplayValue = (
    rangeValue: RangeValue,
    filterTitle?: string
  ) => {
    const { gte, lte } = rangeValue
    let displayValue = ''

    if (gte !== null && lte !== null) {
      displayValue = `${gte}-${lte}`
    } else if (gte !== null) {
      displayValue = `${gte}+`
    } else if (lte !== null) {
      displayValue = `≤${lte}`
    }

    // Add "years" suffix for Experience filter
    if (displayValue && filterTitle === 'Experience') {
      displayValue += ' years'
    }

    return displayValue
  }

  const getDateDisplayValue = (dateValue: {
    gte?: Date | null
    lte?: Date | null
  }) => {
    const { gte, lte } = dateValue
    if (!gte && !lte) return ''

    if (gte && lte) {
      if (dayjs(gte).format('MM/DD/YYYY') === dayjs(lte).format('MM/DD/YYYY')) {
        return dayjs(gte).format('MM/DD/YYYY')
      }
      return `${dayjs(gte).format('MM/DD/YYYY')} - ${dayjs(lte).format(
        'MM/DD/YYYY'
      )}`
    }

    if (gte) return `From ${dayjs(gte).format('MM/DD/YYYY')}`
    if (lte) return `Until ${dayjs(lte).format('MM/DD/YYYY')}`

    return ''
  }

  const filterOptionsBySearch = (
    options: FilterOption[],
    searchText: string
  ) => {
    if (!searchText.trim()) return options

    return options.filter((option) => {
      const label = option.label?.toLowerCase() || ''
      const search = searchText.toLowerCase()

      // For nested options, also search in sub-options
      if (option.options && option.options.length > 0) {
        const subOptionsMatch = option.options.some((subOption: FilterOption) =>
          subOption.label?.toLowerCase().includes(search)
        )
        return label.includes(search) || subOptionsMatch
      }

      return label.includes(search)
    })
  }

  const handleClear = (filter: ApplicantFilter) => {
    if (filter.isRange && filter.onRangeChange) {
      // For range filters, reset to null values
      filter.onRangeChange({
        ...filter.rangeValue,
        gte: null,
        lte: null,
      })
    } else if (filter.isDate && filter.onDateChange) {
      // For date filters, reset to null values
      filter.onDateChange({
        gte: null,
        lte: null,
      })
    } else {
      filter.onChange([])
      filter.dateRange?.onChange({ gte: null, lte: null })
    }
  }

  const handleClearAll = () => {
    onClearAll()
  }

  const menuContents = () => {
    if (selectedSubmenu) {
      const activeFilter = filters.find((f) => f.urlKey === selectedSubmenu)
      if (!activeFilter) return null

      // NB: async filters fetch via loadData() in the submenu-open click
      // handler (below), not here during render — so a failed load can retry
      // on reopen and we never loop by re-firing the fetch every render.

      let optionsToDisplay = [...(activeFilter.options || [])]

      // Filter out options with invalid labels to prevent errors
      optionsToDisplay = optionsToDisplay.filter(
        (option) =>
          option &&
          typeof option.label === 'string' &&
          option.label.trim() !== ''
      )

      // Sort options alphabetically if alphabetize is not explicitly set to false
      if (activeFilter.alphabetize !== false) {
        optionsToDisplay = optionsToDisplay.sort((a, b) => {
          // Handle cases where label might be undefined or null
          const labelA = a.label || ''
          const labelB = b.label || ''
          return labelA.localeCompare(labelB)
        })
      }

      // Handle date filter with calendar interface
      if (activeFilter.isDate && activeFilter.dateValue) {
        return (
          <DateFilter
            onDateChange={activeFilter.onDateChange}
            onClose={() => setSelectedSubmenu('')}
            maxDate={activeFilter.maxDate}
            allowFutureDates={activeFilter.allowFutureDates}
          />
        )
      }

      // Handle zipcode filter with simple text input
      if (activeFilter.isZipcode) {
        return (
          <ZipcodeFilter
            selectedZipcodes={activeFilter.selected}
            newZipcode={activeFilter.newZipcode}
            setNewZipcode={activeFilter.setNewZipcode}
            handleAddZipcode={activeFilter.handleAddZipcode}
            handleRemoveZipcode={activeFilter.handleRemoveZipcode}
            suggestedZipcodes={activeFilter.suggestedZipcodes}
            setSuggestedZipcodes={activeFilter.setSuggestedZipcodes}
            handleAddSuggestedZipcode={activeFilter.handleAddSuggestedZipcode}
            handleAddAllSuggestedZipcodes={
              activeFilter.handleAddAllSuggestedZipcodes
            }
          />
        )
      }

      // Handle range inputs (like Years of Experience and Call Count)
      if (activeFilter.isRange && activeFilter.rangeValue) {
        // Convert local values to numbers for validation
        const minValue = typeof localMin === 'string' ? null : localMin

        return (
          <div className="p-4">
            <div className="flex flex-row items-center gap-2">
              <NumberInput
                placeholder="Min"
                value={localMin}
                onChange={(value) => {
                  setLocalMin(value)
                }}
                min={activeFilter.rangeValue.min}
                max={activeFilter.rangeValue.max}
                size="xs"
                className="flex-1"
              />
              <div className="text-xs text-doubleNickel-gray-500">to</div>
              <NumberInput
                placeholder="Max"
                value={localMax}
                onChange={(value) => {
                  setLocalMax(value)
                }}
                min={Math.max(
                  activeFilter.rangeValue.min,
                  minValue !== null ? minValue : activeFilter.rangeValue.min
                )}
                max={activeFilter.rangeValue.max}
                size="xs"
                className="flex-1"
              />
            </div>
            <div className="mt-3 flex flex-row justify-end">
              <button
                className="rounded bg-doubleNickel-brand-500 px-3 py-1 text-xs text-white hover:bg-doubleNickel-brand-600"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  // Apply the local values to the actual filter
                  const newValue =
                    typeof localMin === 'string' ? null : localMin
                  const newMaxValue =
                    typeof localMax === 'string' ? null : localMax
                  activeFilter.onRangeChange?.({
                    ...activeFilter.rangeValue,
                    gte: newValue,
                    lte: newMaxValue,
                  })
                  // Close the menu after submit
                  setSelectedSubmenu(null)
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )
      }

      // Handle nested options (like Applicant Stage with sub-categories)
      if (activeFilter.isNested) {
        // For server-side search filters (e.g., job listings) the options array
        // already reflects the current search; running filterOptionsBySearch
        // again would re-filter on its own search text and double-trim.
        const filteredOptions = activeFilter.isServerSideSearch
          ? optionsToDisplay
          : filterOptionsBySearch(optionsToDisplay, searchText)

        // Async nested filters (e.g. Lead Source, which fetches via loadData)
        // should show "Loading…" while the initial query is in flight instead
        // of flashing "No options found" — matching the flat async path below.
        const isNestedInitialLoading =
          !!activeFilter.loadData &&
          !activeFilter.isLoaded &&
          filteredOptions.length === 0

        return (
          <>
            {isNestedInitialLoading ? (
              <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
                Loading…
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div key={option.value} className="flex flex-col">
                  <Box
                    className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
                    onClick={() => {
                      // For steps, don't add parent to selection - just expand/collapse
                      if (activeFilter.urlKey === 'steps') {
                        // Toggle expansion for step filters
                        setExpandedSteps((prev) => {
                          const newExpanded = new Set(prev)
                          if (newExpanded.has(option.value)) {
                            newExpanded.delete(option.value)
                          } else {
                            newExpanded.add(option.value)
                          }
                          return newExpanded
                        })
                        return
                      }

                      // For applicant stages, don't add parent to selection - just expand/collapse
                      if (activeFilter.urlKey === 'applicantStages') {
                        // Toggle expansion for applicant stage filters
                        setExpandedApplicantStages((prev) => {
                          const newExpanded = new Set(prev)
                          if (newExpanded.has(option.value)) {
                            newExpanded.delete(option.value)
                          } else {
                            newExpanded.add(option.value)
                          }
                          return newExpanded
                        })
                        return
                      }

                      // For lead sources, don't add parent to selection - just expand/collapse
                      if (activeFilter.urlKey === 'leadSources') {
                        // Toggle expansion for lead source (link type) filters
                        setExpandedLeadSources((prev) => {
                          const newExpanded = new Set(prev)
                          if (newExpanded.has(option.value)) {
                            newExpanded.delete(option.value)
                          } else {
                            newExpanded.add(option.value)
                          }
                          return newExpanded
                        })
                        return
                      }

                      const isCurrentlySelected =
                        activeFilter.selected.includes(option.value)

                      if (isCurrentlySelected) {
                        // When deselecting parent, remove both parent and all its sub-options
                        const newSelected = activeFilter.selected.filter(
                          (item) => {
                            // Remove the parent value
                            if (item === option.value) return false
                            // Remove all sub-options that start with parent:sub
                            if (
                              item.includes(':') &&
                              item.startsWith(`${option.value}:`)
                            ) {
                              return false
                            }
                            return true
                          }
                        )
                        activeFilter.onChange(newSelected)
                      } else {
                        // When selecting parent, just add the parent value
                        const newSelected = [
                          ...activeFilter.selected,
                          option.value,
                        ]
                        activeFilter.onChange(newSelected)
                      }
                    }}
                  >
                    {option.label}
                    {activeFilter.urlKey !== 'steps' &&
                      activeFilter.urlKey !== 'applicantStages' &&
                      activeFilter.urlKey !== 'leadSources' &&
                      activeFilter.selected.includes(option.value) && (
                        <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                      )}
                  </Box>
                  {option.options &&
                    option.options.length > 0 &&
                    (activeFilter.urlKey === 'steps'
                      ? expandedSteps.has(option.value)
                      : activeFilter.urlKey === 'applicantStages'
                      ? expandedApplicantStages.has(option.value)
                      : activeFilter.urlKey === 'leadSources'
                      ? expandedLeadSources.has(option.value)
                      : activeFilter.selected.includes(option.value)) && (
                      <div className="ml-4 flex flex-col">
                        {/* Add "All" option for applicant stages and lead sources */}
                        {(activeFilter.urlKey === 'applicantStages' ||
                          activeFilter.urlKey === 'leadSources') && (
                          <Box
                            className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-50"
                            onClick={() => {
                              const isCurrentlySelected =
                                activeFilter.selected.includes(option.value)

                              if (isCurrentlySelected) {
                                // When deselecting "All", only remove the parent value, keep sub-options
                                const newSelected =
                                  activeFilter.selected.filter(
                                    (item) => item !== option.value
                                  )
                                activeFilter.onChange(newSelected)
                              } else {
                                // When selecting "All", just add the parent value
                                const newSelected = [
                                  ...activeFilter.selected,
                                  option.value,
                                ]
                                activeFilter.onChange(newSelected)
                              }
                            }}
                          >
                            <span className="italic text-doubleNickel-gray-600">
                              All
                            </span>
                            {activeFilter.selected.includes(option.value) && (
                              <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                            )}
                          </Box>
                        )}
                        {option.options
                          .filter((subOption) => subOption.value !== 'ALL')
                          .map((subOption) => (
                            <Box
                              key={subOption.value}
                              className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-50"
                              onClick={() => {
                                const subValue = `${option.value}:${subOption.value}`
                                const newSelected =
                                  activeFilter.selected.includes(subValue)
                                    ? activeFilter.selected.filter(
                                        (item) => item !== subValue
                                      )
                                    : [...activeFilter.selected, subValue]
                                activeFilter.onChange(newSelected)
                              }}
                            >
                              {subOption.label}
                              {activeFilter.selected.includes(
                                `${option.value}:${subOption.value}`
                              ) && (
                                <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                              )}
                            </Box>
                          ))}
                      </div>
                    )}
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
                {searchText.trim()
                  ? 'No matching options found'
                  : 'No options found'}
              </div>
            )}
          </>
        )
      }

      // Server-side search filters skip the client-side filter — the options
      // already reflect the current search.
      const filteredOptions = activeFilter.isServerSideSearch
        ? optionsToDisplay
        : filterOptionsBySearch(optionsToDisplay, searchText)

      // For async filters (those that fetch via loadData), show "Loading…"
      // while the initial query is in flight. Without this, options.length===0
      // falls through to "No options found" — which reads as "empty" and
      // hides the fact that the menu is fetching. Particularly noticeable
      // for the paginated Position filter against companies with many
      // listings, where the initial query can take a few seconds.
      const isInitialLoading =
        !!activeFilter.loadData &&
        !activeFilter.isLoaded &&
        filteredOptions.length === 0

      return (
        <>
          {isInitialLoading ? (
            <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
              Loading…
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <Box
                key={option.value}
                className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
                onClick={() => {
                  const newSelected = activeFilter.selected.includes(
                    option.value
                  )
                    ? activeFilter.selected.filter(
                        (item) => item !== option.value
                      )
                    : [...activeFilter.selected, option.value]
                  activeFilter.onChange(newSelected)
                }}
              >
                {option.label}
                {activeFilter.selected.includes(option.value) && (
                  <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                )}
              </Box>
            ))
          ) : (
            <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
              {searchText.trim()
                ? 'No matching options found'
                : 'No options found'}
            </div>
          )}
          {activeFilter.dateRange && (
            <DateFilter
              onDateChange={activeFilter.dateRange.onChange}
              onClose={() => setSelectedSubmenu('')}
              allowFutureDates
              presets={[]}
            />
          )}
          {activeFilter.onLoadMore && activeFilter.hasMore && (
            <div ref={loadMoreSentinelRef} className="h-px" />
          )}
          {activeFilter.loadingMore && (
            <div className="flex items-center justify-center p-2 text-xs text-doubleNickel-gray-500">
              Loading more…
            </div>
          )}
        </>
      )
    }

    // Filter categories by search text
    const filteredFilters = filters
      .slice()
      .filter((filter) =>
        filterCategorySearch.trim()
          ? filter.title
              .toLowerCase()
              .includes(filterCategorySearch.toLowerCase())
          : true
      )
      .sort((a, b) => a.title.localeCompare(b.title))

    return (
      <>
        {filteredFilters.length > 0 ? (
          filteredFilters.map((filter) => (
            <Menu.Item
              key={filter.urlKey}
              rightSection={
                <IconChevronLeft className="rotate-180 fill-none stroke-doubleNickel-gray-400" />
              }
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setSelectedSubmenu(filter.urlKey)
                setFilterCategorySearch('') // Clear search when entering submenu
                // Fetch (or retry) async options on open; the hook guards it.
                filter.loadData?.()
              }}
            >
              <Box className="flex flex-row items-center justify-between py-1 text-xs font-medium text-doubleNickel-gray-800">
                {filter.title}
                <div className="max-w-[200px] flex-shrink-0 overflow-hidden truncate whitespace-nowrap font-normal text-doubleNickel-gray-500">
                  {filter.isRange && filter.rangeValue ? (
                    getRangeDisplayValue(filter.rangeValue, filter.title)
                  ) : filter.isDate && filter.dateValue ? (
                    getDateDisplayValue(filter.dateValue)
                  ) : (
                    <>
                      {filter.title === 'Employees' ? (
                        filter.selected?.length > 0 &&
                        `${filter.selected.length} ${
                          filter.selected.length === 1
                            ? 'employee'
                            : 'employees'
                        }`
                      ) : filter.title === 'Position' ? (
                        filter.selected?.length > 0 &&
                        `${filter.selected.length} position${
                          filter.selected.length === 1 ? '' : 's'
                        }`
                      ) : filter.title === 'Applicant Stage' ? (
                        filter.selected?.length > 0 &&
                        `${filter.selected.length} ${
                          filter.selected.length === 1 ? 'stage' : 'stages'
                        }`
                      ) : filter.title === 'Steps' ? (
                        filter.selected?.length > 0 &&
                        `${filter.selected.length} ${
                          filter.selected.length === 1 ? 'step' : 'steps'
                        }`
                      ) : filter.title === 'Lead Source' ? (
                        filter.selected?.length > 0 &&
                        `${filter.selected.length} selected`
                      ) : (
                        <>
                          {filter.selected?.length > 0 &&
                            getDisplayValue(filter, filter.selected[0])}
                          {filter.selected?.length > 1 &&
                            `, ${filter.selected.length - 1}+`}
                          {hasDateRangeValue(filter) &&
                            `${
                              filter.selected?.length ? ', ' : ''
                            }${getDateDisplayValue(filter.dateRange.value)}`}
                        </>
                      )}
                    </>
                  )}
                </div>
              </Box>
            </Menu.Item>
          ))
        ) : (
          <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
            No matching filters found
          </div>
        )}
      </>
    )
  }

  return (
    <Menu
      shadow="md"
      width={350}
      position="bottom-end"
      withArrow
      arrowPosition="center"
      closeOnItemClick={false}
      closeOnClickOutside={true}
    >
      <Menu.Target>
        <DNButton variant="outline">
          <IconFilter className="h-4 fill-none stroke-doubleNickel-gray-700" />
          Filters
          <div className="rounded-lg bg-doubleNickel-gray-100 px-1 py-0.5 text-[10px] text-doubleNickel-gray-500">
            {getTotalSelectedCount()}
          </div>
        </DNButton>
      </Menu.Target>

      <Menu.Dropdown className="rounded-lg">
        <div className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center pr-4 text-sm">
            {selectedSubmenu && (
              <ActionIcon
                variant="subtle"
                onClick={() => setSelectedSubmenu('')}
              >
                <IconChevronLeft className="fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            )}
            <Menu.Label>
              {selectedSubmenu
                ? filters.find((f) => f.urlKey === selectedSubmenu)?.title ||
                  'Filters'
                : 'Filters'}
            </Menu.Label>
            <Box
              className="ml-auto text-xs text-doubleNickel-brand-500 hover:cursor-pointer hover:underline"
              onClick={() => {
                if (selectedSubmenu) {
                  const filter = filters.find(
                    (f) => f.urlKey === selectedSubmenu
                  )
                  if (filter) handleClear(filter)
                } else {
                  handleClearAll()
                }
              }}
            >
              {selectedSubmenu ? 'Clear' : 'Clear all'}
            </Box>
          </div>
          {selectedSubmenu &&
            (() => {
              const activeFilter = filters.find(
                (f) => f.urlKey === selectedSubmenu
              )
              const shouldShowSearch =
                activeFilter &&
                !activeFilter.isDate &&
                !activeFilter.isZipcode &&
                !activeFilter.isRange &&
                activeFilter.options

              return (
                <>
                  {shouldShowSearch && (
                    <div className="px-3">
                      <TextInput
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => {
                          const value = e.target.value
                          setSearchText(value)
                          // Forward to the active filter's server-side handler
                          // (Position) when it has one. Done inline rather than
                          // in an effect so the handler is only called on real
                          // user input, not when `filters` re-references after
                          // a search response lands.
                          const activeFilter = filters.find(
                            (f) => f.urlKey === selectedSubmenu
                          )
                          activeFilter?.onSearchChange?.(value)
                        }}
                        size="xs"
                        className="w-full"
                      />
                    </div>
                  )}
                  <div
                    ref={scrollContainerRef}
                    className="max-h-[450px] overflow-y-auto"
                  >
                    {menuContents()}
                  </div>
                </>
              )
            })()}
          {!selectedSubmenu && (
            <>
              <div className="px-3">
                <TextInput
                  placeholder="Search filters..."
                  value={filterCategorySearch}
                  onChange={(e) => setFilterCategorySearch(e.target.value)}
                  size="xs"
                  className="w-full"
                />
              </div>
              <div className="max-h-[450px] overflow-y-auto">
                {menuContents()}
              </div>
            </>
          )}
        </div>
      </Menu.Dropdown>
    </Menu>
  )
}

export default ApplicantFilterMenu
